import { Splide, SplideProps, SplideSlide } from '@splidejs/react-splide'
import { Options as SplideOptions } from '@splidejs/splide'
import first from 'lodash/first'
import includes from 'lodash/includes'
import join from 'lodash/join'
import map from 'lodash/map'
import merge from 'lodash/merge'
import React, { forwardRef, Ref, useState } from 'react'
import styled, { css } from 'styled-components'

import { ImageV2 } from '@Components/Image'
import { Product } from '@Generated/graphql'
import { blackImageOverlay } from '@Globals/Style/mixins'

import { ThumbnailSlide } from './components/sliders/ThumbnailSlide'
import { Slider } from './Slider'

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  min-height: 400px;
  height: 44.5vw;
  max-height: 586px;
  max-width: 78px;
`

const StyledSlider = styled(Slider)<{ arrowSpacing: boolean }>(({
  arrowSpacing,
}) => css`
  width: 100%;

  ${!arrowSpacing && css`
    padding: 0;
  `}
  .splide__arrows--ttb.splide__arrows .splide__arrow--prev,
  .splide__arrows--ttb.splide__slider > .splide__track > .splide__arrows .splide__arrow--prev,
  .splide__arrows--ttb.splide__track > .splide__arrows .splide__arrow--prev {
    top: 0;
  }

  .splide__arrows--ttb.splide__arrows .splide__arrow--next,
  .splide__arrows--ttb.splide__slider > .splide__track > .splide__arrows .splide__arrow--next,
  .splide__arrows--ttb.splide__track > .splide__arrows .splide__arrow--next {
    bottom: 0;
  }

  .splide__arrow {
    width: 100%;
    height: 27px;

    svg {
      width: 15px
    }
  }

  .splide__list {
    padding-top: 30px;
  }

  .splide__slide {
    margin-bottom: 8px;

    &.is-active:after, &.is-over:after {
      content: '';
      display: block;
      height: 3px;
      width: 100%;
      background-color: #146c92;
      position: absolute;
      bottom: 0;
      left: 0;
      transition: opacity .15s ease;
    }
  }

  .splide__slide img {
    height: auto;
    width: 100%;
  }
`)

interface VerticalSliderProp extends SplideProps, Pick<Product, 'assets'> {
  ref?: Ref<Splide>
  title?: string
}

const defaultProps: SplideOptions = {
  options: {
    arrowPath: 'M11,0L8.4,2.7L26.2,20L8.4,37.3L11,40l20.6-20L11,0z',
    direction: 'ttb',
    height: '100%',
    keyboard: false,
    pagination: false,
  },
}

const BlackOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 100;

  ${blackImageOverlay}
  & + span {
    display: block !important;
  }
`

export const VerticalSlider: React.FC<VerticalSliderProp> = forwardRef(({
  assets,
  title,
  ...componentProps
}: VerticalSliderProp, ref: Ref<Splide>) => {
  const disablePagination = assets && assets.length <= 6
  const props = merge(
    {},
    defaultProps,
    { ...disablePagination ? { options: { ...componentProps.options, focus: 0 } } : componentProps },
    { assets: assets?.length },
  )
  const [arrowSpacing, setArrowSpacing] = useState(false)

  return (
    <StyledWrapper>
      <StyledSlider
        arrowSpacing={arrowSpacing}
        onArrowsMounted={() => setArrowSpacing(true)}
        {...props}
        ref={ref}
      >
        {map(assets, ({ tags, url }, index) => {
          const altText = `${title || ''} - ${index + 1} - ${join(tags, ' ')}`.trim()

          return (
            <SplideSlide key={url}>
              <BlackOverlay />
              {includes(tags, 'video')
                ? <ThumbnailSlide alt={altText} index={index} source={first(assets)?.url || ''} />
                : (
                  <ImageV2
                    alt={altText}
                    draggable={false}
                    height={78}
                    objectFit="contain"
                    src={url}
                    width={78}
                  />
                )}
            </SplideSlide>
          )
        })}
      </StyledSlider>
    </StyledWrapper>
  )
})
